import React, { useState, useEffect } from "react";
import { Globe, Loader2, Check, X, Save, Trash2, Play, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { API } from "@/App";
import { toast } from "sonner";

const SEARCH_TYPES = [
  { value: "new_ais", label: "New AIs only" },
  { value: "updates", label: "Updates only (existing AIs)" },
  { value: "both", label: "Both (new + updates)" },
  { value: "prompting_techniques", label: "New prompting techniques" },
  { value: "seo", label: "SEO tools" },
  { value: "news", label: "News feed items" },
];

const PURPOSES = [
  { value: "prompt_matching", label: "Prompt Matching" },
  { value: "prompt_optimization", label: "Prompt Optimization" },
  { value: "seo", label: "SEO" },
];

const FLAG_STYLES = {
  NEW: "bg-neon/10 text-neon border-neon/30",
  UPDATE: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  DELETE: "bg-red-500/10 text-red-400 border-red-500/30",
  NEWS: "bg-blue-500/10 text-blue-300 border-blue-500/30",
};

export const WebsearchTab = ({ tools = [], categories = [] }) => {
  const [form, setForm] = useState({ type: "new_ais", purpose: "prompt_matching", query: "", category: "all", limit: "5" });
  const [specificTools, setSpecificTools] = useState([]);
  const [running, setRunning] = useState(false);
  const [queue, setQueue] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [actioning, setActioning] = useState(null);

  useEffect(() => { fetchQueue(); fetchTemplates(); }, []);

  const fetchQueue = async () => {
    const res = await fetch(`${API}/admin/websearch/queue?status=pending_review`, { credentials: "include" });
    if (res.ok) setQueue(await res.json());
  };

  const fetchTemplates = async () => {
    const res = await fetch(`${API}/admin/websearch/templates`, { credentials: "include" });
    if (res.ok) setTemplates(await res.json());
  };

  const toggleTool = (name) => {
    setSpecificTools(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  };

  const runSearch = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${API}/admin/websearch`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          type: form.type, purpose: form.purpose, query: form.query,
          category: form.category === "all" ? "" : form.category,
          specific_tools: specificTools,
          limit: form.limit === "unlimited" ? 0 : parseInt(form.limit),
        }),
      });
      const data = await res.json();
      if (res.ok) { toast.success(`${data.count} items added to review queue`); fetchQueue(); }
      else toast.error(data.detail || "Websearch failed");
    } catch (e) { toast.error("Websearch failed — try a smaller result limit"); }
    finally { setRunning(false); }
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) { toast.error("Template name required"); return; }
    const res = await fetch(`${API}/admin/websearch/templates`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ name: templateName.trim(), config: { ...form, specific_tools: specificTools } }),
    });
    if (res.ok) { toast.success("Template saved"); setTemplateName(""); fetchTemplates(); }
  };

  const applyTemplate = (tpl) => {
    const c = tpl.config || {};
    setForm({ type: c.type || "new_ais", purpose: c.purpose || "prompt_matching", query: c.query || "", category: c.category || "all", limit: c.limit || "5" });
    setSpecificTools(c.specific_tools || []);
    toast.info(`Template "${tpl.name}" applied`);
  };

  const deleteTemplate = async (id) => {
    const res = await fetch(`${API}/admin/websearch/templates/${id}`, { method: "DELETE", credentials: "include" });
    if (res.ok) { toast.success("Template deleted"); fetchTemplates(); }
  };

  const handleQueueAction = async (queueId, action) => {
    setActioning(queueId);
    try {
      const res = await fetch(`${API}/admin/websearch/queue/${queueId}/${action}`, { method: "POST", credentials: "include" });
      const data = await res.json();
      if (res.ok) {
        toast.success(action === "approve" ? `Approved${data.users_notified ? ` · ${data.users_notified} users notified` : ""}` : "Rejected");
        fetchQueue();
      } else toast.error(data.detail || "Failed");
    } catch (e) { toast.error("Something went wrong"); }
    finally { setActioning(null); }
  };

  return (
    <div className="space-y-6" data-testid="websearch-tab">
      {/* Search form */}
      <div className="bg-dark-paper border border-white/10 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4"><Globe className="w-4 h-4 text-purple-400" /><h2 className="font-semibold">AI Websearch</h2>
          <span className="text-[10px] text-white/30 ml-2">Manual runs · results go to the review queue below</span>
        </div>
        <div className="grid md:grid-cols-4 gap-3 mb-3">
          <div>
            <Label className="text-xs">Search option</Label>
            <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
              <SelectTrigger className="bg-dark-subtle border-white/10 text-xs" data-testid="ws-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{SEARCH_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Purpose</Label>
            <Select value={form.purpose} onValueChange={v => setForm({ ...form, purpose: v })}>
              <SelectTrigger className="bg-dark-subtle border-white/10 text-xs" data-testid="ws-purpose-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PURPOSES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Category filter</Label>
            <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
              <SelectTrigger className="bg-dark-subtle border-white/10 text-xs" data-testid="ws-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Result limit</Label>
            <Select value={form.limit} onValueChange={v => setForm({ ...form, limit: v })}>
              <SelectTrigger className="bg-dark-subtle border-white/10 text-xs" data-testid="ws-limit-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["5", "10", "25", "50", "100"].map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                <SelectItem value="unlimited">Unlimited</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[9px] text-white/25 mt-0.5">Runs are capped at 25/batch to stay fast — re-run for more</p>
          </div>
        </div>
        <div className="mb-3">
          <Label className="text-xs">Search query {["updates", "both"].includes(form.type) && <span className="text-white/30">(optional for updates)</span>}</Label>
          <Input value={form.query} onChange={e => setForm({ ...form, query: e.target.value })} placeholder="e.g., coding assistants, image generation, medical AI…" className="bg-dark-subtle border-white/10 text-sm" data-testid="ws-query-input" />
        </div>
        {["updates", "both"].includes(form.type) && tools.length > 0 && (
          <div className="mb-3">
            <Label className="text-xs mb-1.5 block">Specific AIs (leave empty for all)</Label>
            <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto p-2 bg-dark rounded-lg border border-white/5">
              {tools.map(t => (
                <label key={t.tool_id} className="flex items-center gap-1.5 text-xs text-white/60 cursor-pointer" data-testid={`ws-tool-check-${t.tool_id}`}>
                  <Checkbox checked={specificTools.includes(t.name)} onCheckedChange={() => toggleTool(t.name)} />
                  {t.name}
                </label>
              ))}
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={runSearch} disabled={running} className="bg-purple-700 text-white hover:bg-purple-600 text-xs" data-testid="ws-run-btn">
            {running ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Searching…</> : <><Play className="w-3.5 h-3.5 mr-1" />Run Websearch</>}
          </Button>
          <div className="flex items-center gap-2 ml-auto">
            <Input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="Template name" className="bg-dark-subtle border-white/10 text-xs w-36 h-8" data-testid="ws-template-name" />
            <Button onClick={saveTemplate} variant="outline" size="sm" className="border-white/20 text-xs" data-testid="ws-save-template"><Save className="w-3 h-3 mr-1" />Save as Template</Button>
          </div>
        </div>
        {templates.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-white/5">
            <span className="text-[10px] text-white/30 mt-1">Templates:</span>
            {templates.map(tpl => (
              <div key={tpl.template_id} className="flex items-center gap-1 bg-dark rounded-full border border-white/10 pl-3 pr-1 py-0.5">
                <button onClick={() => applyTemplate(tpl)} className="text-xs text-purple-300 hover:text-purple-200" data-testid={`ws-template-${tpl.template_id}`}>{tpl.name}</button>
                <button onClick={() => deleteTemplate(tpl.template_id)} className="p-1 text-white/30 hover:text-red-400"><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review queue */}
      <div className="bg-dark-paper border border-white/10 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">Review Queue ({queue.length})</h2>
          <Button onClick={fetchQueue} variant="ghost" size="sm" className="text-white/40 text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
        </div>
        {queue.length === 0 ? (
          <p className="text-white/40 text-sm text-center py-6">Queue is empty. Run a websearch to populate it.</p>
        ) : (
          <div className="space-y-3">
            {queue.map(item => {
              const d = item.data || {};
              return (
                <div key={item.queue_id} className="p-4 bg-dark rounded-lg border border-white/5" data-testid={`ws-queue-${item.queue_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge className={`text-[10px] border ${FLAG_STYLES[item.flag] || FLAG_STYLES.NEWS}`}>{item.flag || "NEWS"}</Badge>
                        <span className="font-semibold text-sm">{d.name || d.title || "Untitled"}</span>
                        {d.version && <Badge variant="outline" className="text-[9px] border-white/20">v{d.version}</Badge>}
                        {d.category && <Badge variant="outline" className="text-[9px] border-white/20">{d.category}</Badge>}
                        {d.price && <Badge variant="outline" className="text-[9px] border-purple-500/30 text-purple-300">{d.price}</Badge>}
                      </div>
                      <p className="text-xs text-white/50 leading-relaxed">{d.summary || d.description || d.general_info || ""}</p>
                      {d.changes && <p className="text-[10px] text-amber-400/70 mt-1">Changes: {Object.keys(d.changes).join(", ")}</p>}
                      {(d.reasoning_model || d.features?.length > 0) && (
                        <p className="text-[10px] text-white/30 mt-1">
                          {d.reasoning_model && <>Reasoning: <span className="text-purple-300">{d.reasoning_model}</span> · </>}
                          {d.features?.length > 0 && <>Features: {d.features.slice(0, 4).join(", ")}</>}
                        </p>
                      )}
                      {(d.source_url || d.website_url) && <a href={d.source_url || d.website_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-purple-400 hover:underline">{d.source_url || d.website_url}</a>}
                    </div>
                    <div className="flex gap-1.5">
                      <Button onClick={() => handleQueueAction(item.queue_id, "approve")} disabled={actioning === item.queue_id} size="sm" className="bg-neon/15 text-neon hover:bg-neon/25 text-xs" data-testid={`ws-approve-${item.queue_id}`}><Check className="w-3.5 h-3.5" /></Button>
                      <Button onClick={() => handleQueueAction(item.queue_id, "reject")} disabled={actioning === item.queue_id} size="sm" variant="outline" className="border-red-500/30 text-red-400 text-xs" data-testid={`ws-reject-${item.queue_id}`}><X className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WebsearchTab;
